// Pure logic behind the My Access detail cards — time/status math, the truthful
// activity-timeline builders, and the History gate. Kept free of React, Chakra
// and the state/api layer so it can be unit-tested without a DOM (the state
// layer reads localStorage at import, which a node test env can't provide).

import { IconType } from "react-icons";
import {
  TbAlertTriangle,
  TbCircleCheck,
  TbCircleX,
  TbClockX,
  TbInfoCircle,
  TbKey,
  TbPlayerSkipForward,
  TbSend,
  TbSettings,
  TbShieldCheck,
  TbUserMinus,
} from "react-icons/tb";

import { type ChipTone } from "components/ds/Chip";
import type { BarTone } from "components/ds/Bar";
import { pluralise } from "modules/utils";
import type {
  MFAccessRequest,
  MFSelectableResourceAccess,
} from "state/members/access_v2/catalogs";
import type { MFPriorRequests, MFTeamAdoption } from "state/members/tasks";

const DAY_MS = 86_400_000;

export interface AccessMetrics {
  indefinite: boolean;
  daysLeft: number;
  durationDays: number;
}

// The time math the row, grouping, status card and drawer all rely on.
export const accessMetrics = (a: MFSelectableResourceAccess): AccessMetrics => {
  const expiresMs = a.expiresAt ? new Date(a.expiresAt).getTime() : null;
  const grantedMs = a.grantedAt ? new Date(a.grantedAt).getTime() : null;
  const indefinite = Boolean(a.isIndefinite) || !expiresMs;
  return {
    indefinite,
    daysLeft: expiresMs ? (expiresMs - Date.now()) / DAY_MS : Infinity,
    durationDays:
      expiresMs && grantedMs ? (expiresMs - grantedMs) / DAY_MS : 0,
  };
};

// Time-remaining label for an active grant's status card. Mirrors the row list
// (AccessGroup): whole days from a day out, hours under a day, then a
// graduated "less than …" ladder for the final hour — "0h left" told the user
// nothing about whether that meant fifty minutes or fifty seconds.
export const remainingLabel = (daysLeft: number): string => {
  if (daysLeft >= 1) {
    const days = Math.round(daysLeft);
    return `${days} ${pluralise("day", days)} left`;
  }
  const hoursLeft = daysLeft * 24;
  if (hoursLeft >= 1) return `${Math.round(hoursLeft)}h left`;
  const minutesLeft = hoursLeft * 60;
  if (minutesLeft >= 10) return "less than an hour left";
  if (minutesLeft >= 1) return "less than 10 minutes left";
  // Also the floor for an expired-but-unswept grant: the backend removes it
  // within moments, and "0h left" was the exact label this ladder replaces.
  return "less than a minute left";
};

// Urgency thresholds. The absolute ones read as calendar time ("under a week
// left"); the fractional ones as a share of the grant's own window.
const ERROR_DAYS = 7;
const WARNING_DAYS = 30;
const ERROR_FRACTION = 0.15;
const WARNING_FRACTION = 0.4;

// One rule or the other, never a mix of both.
//
// A grant that outlives the calendar thresholds is judged by them, exactly as
// before: under 30 days is a warning, under 7 is urgent. A grant SHORTER than
// those thresholds can't be judged by them at all — on a one-hour grant "under
// 7 days left" is true from the first second, which painted the bar red for
// the grant's entire life — so it is judged on how much of its own window is
// left instead.
//
// Blending the two would misjudge the long end just as badly: a year-long
// grant is 15% spent with 54 days still to run, which is not urgent.
const urgencyTone = (
  daysLeft: number,
  windowDays: number,
  fractionLeft: number,
): BarTone => {
  if (daysLeft <= 0) return "error";
  if (windowDays > WARNING_DAYS) {
    if (daysLeft <= ERROR_DAYS) return "error";
    if (daysLeft <= WARNING_DAYS) return "warning";
    return "success";
  }
  if (fractionLeft <= ERROR_FRACTION) return "error";
  if (fractionLeft <= WARNING_FRACTION) return "warning";
  return "success";
};

export interface RemainingBar {
  /** How much of the grant window is still to run, 0–100. */
  pct: number;
  tone: BarTone;
}

// The time-remaining bar shown on the My Access rows, the Home "expiring soon"
// list and the grant drawer. The bar DRAINS: full when the grant starts, empty
// when it expires.
//
// The denominator is the grant's own window. It used to be floored at one day,
// which silently turned every sub-day grant into a near-empty bar the moment
// it was granted (a fresh 1-hour grant drew ~4% with 52 minutes still to run,
// while the same grant drew ~87% elsewhere — the two screens disagreed because
// only one of them floored).
export const remainingBar = (m: AccessMetrics): RemainingBar => {
  if (m.indefinite) return { pct: 100, tone: "success" };
  // No usable window (no grantedAt) — there's nothing to take a fraction of,
  // so judge urgency on calendar time alone and show the bar full while there
  // is time left, empty once there isn't.
  if (!(m.durationDays > 0)) {
    const pct = m.daysLeft > 0 ? 100 : 0;
    return { pct, tone: urgencyTone(m.daysLeft, Infinity, 1) };
  }
  const fractionLeft = Math.min(1, Math.max(0, m.daysLeft / m.durationDays));
  return {
    pct: fractionLeft * 100,
    tone: urgencyTone(m.daysLeft, m.durationDays, fractionLeft),
  };
};

// The text colour that goes with a bar tone (the "3 days left" label beside
// the bar, on both the My Access rows and the Home card).
export const urgencyTextColor = (tone: BarTone): string =>
  tone === "error"
    ? "error.700"
    : tone === "warning"
      ? "warning.800"
      : "neutral.800";

export interface StatusInfo {
  label: string;
  tone: ChipTone;
  barTone: BarTone;
}

export const statusInfo = (access: MFSelectableResourceAccess): StatusInfo => {
  const m = accessMetrics(access);
  if (access.status === "expired")
    return { label: "Expired", tone: "error", barTone: "error" };
  if (m.indefinite)
    return { label: "Active · Indefinite", tone: "success", barTone: "success" };
  // Same urgency the bar uses, so the chip and the bar never contradict.
  const { tone } = remainingBar(m);
  if (tone === "error")
    return { label: "Expiring soon", tone: "error", barTone: "error" };
  if (tone === "warning")
    return {
      label: remainingLabel(m.daysLeft),
      tone: "warning",
      barTone: "warning",
    };
  return { label: "Active", tone: "success", barTone: "success" };
};

export type RequestTone = "warning" | "success" | "error" | "neutral";

export interface RequestStatusInfo {
  chip: string;
  heading: string;
  tone: RequestTone;
}

export const REQUEST_STATUS_INFO: Record<
  MFAccessRequest["status"],
  RequestStatusInfo
> = {
  awaiting_signature: {
    chip: "Needs your signature",
    heading: "Waiting for you to sign with your passkey",
    tone: "warning",
  },
  pending: {
    chip: "Pending approval",
    heading: "Awaiting approval",
    tone: "warning",
  },
  approved: { chip: "Approved", heading: "Approved", tone: "success" },
  provisioning: {
    chip: "Approved",
    heading: "Approved · setting up access",
    tone: "success",
  },
  provisioned: { chip: "Granted", heading: "Access granted", tone: "success" },
  failed: { chip: "Failed", heading: "Couldn't set up access", tone: "error" },
  refused: { chip: "Refused", heading: "Request refused", tone: "error" },
  rejected: { chip: "Rejected", heading: "Request rejected", tone: "error" },
  cancelled: {
    chip: "Cancelled",
    heading: "Request cancelled",
    tone: "neutral",
  },
  deprovisioning: {
    chip: "Removing",
    heading: "Removing access",
    tone: "neutral",
  },
  deprovisioned: {
    chip: "Removed",
    heading: "Access removed",
    tone: "neutral",
  },
  expired: { chip: "Expired", heading: "Request expired", tone: "neutral" },
};

export const requestChipTone = (tone: RequestTone): ChipTone =>
  tone === "neutral" ? "default" : tone;

// Whether two timestamps land on the same calendar day (local time). Used to
// disambiguate a same-day grant window, where date-only rows would both read
// e.g. "Jul 10, 2026".
const sameCalendarDay = (a?: string | null, b?: string | null): boolean => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

// The Granted/Expires rows on the status card. When the grant is granted and
// expires on the SAME calendar day (a sub-24h grant) both rows would otherwise
// show an identical date — include the time so they're distinguishable. Multi-
// day grants stay date-only; indefinite grants never expire.
export const grantWindow = (
  a: MFSelectableResourceAccess,
): { granted: string; expires: string } => {
  const indefinite = Boolean(a.isIndefinite) || !a.expiresAt;
  const fmt = !indefinite && sameCalendarDay(a.grantedAt, a.expiresAt)
    ? fmtDateTime
    : fmtDate;
  return {
    granted: fmt(a.grantedAt),
    expires: indefinite ? "Never" : fmt(a.expiresAt),
  };
};

export const expiredAgoLabel = (iso?: string | null): string => {
  const ms = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(ms)) return "Expired";
  const d = (Date.now() - ms) / DAY_MS;
  if (d < 1) return "Expired today";
  if (d < 30) return `Expired ${Math.round(d)} ${pluralise("day", Math.round(d))} ago`;
  if (d < 365) return `Expired ${Math.round(d / 30)} ${pluralise("month", Math.round(d / 30))} ago`;
  return `Expired ${Math.round(d / 365)} ${pluralise("year", Math.round(d / 365))} ago`;
};

// One entry in the activity timeline — presentation is resolved here so the
// timeline component stays a dumb, truthful renderer of whatever events exist.
export interface TimelineEvent {
  id: string;
  icon: IconType;
  color: string;
  label: string;
  detail?: string | null;
  at: string;
}

// Truthful timeline for a grant — only the events actually backed by the grant
// row: it was granted, and (if it has lapsed) it expired. No fabricated cycles.
export const buildGrantActivity = (
  access: MFSelectableResourceAccess,
): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  if (access.grantedAt) {
    events.push({
      id: "granted",
      icon: TbKey,
      color: "success.600",
      label: "Access granted",
      detail: "Access provisioned",
      at: access.grantedAt,
    });
  }
  if (access.status === "expired" && access.expiresAt) {
    events.push({
      id: "expired",
      icon: TbClockX,
      color: "error.500",
      label: "Access expired",
      detail: "Access lapsed",
      at: access.expiresAt,
    });
  }
  return events.sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );
};

// The grant detail drawer's activity timeline. When the grant came through the
// request flow (originatingRequestId present → the request was fetched), render
// that request's real lifecycle events. Otherwise (auto-granted, request not yet
// loaded, or a request with no events) fall back to the minimal grant-only
// timeline so the section is never empty and never crashes.
export const resolveGrantActivity = (
  access: MFSelectableResourceAccess,
  request?: MFAccessRequest | null,
): TimelineEvent[] => {
  const timeline = toTimeline(request);
  return timeline.length > 0 ? timeline : buildGrantActivity(access);
};

type EventStatus = MFAccessRequest["events"][number]["status"];

// Presentation for each server event status. `message` (server-supplied)
// becomes the detail line under the label.
export const EVENT_META: Record<
  EventStatus,
  { icon: IconType; color: string; label: string }
> = {
  created: { icon: TbSend, color: "azurite.500", label: "Request created" },
  waiting_approval: {
    icon: TbShieldCheck,
    color: "warning.500",
    label: "Awaiting approval",
  },
  approved: { icon: TbCircleCheck, color: "success.500", label: "Approved" },
  rejected: { icon: TbCircleX, color: "error.500", label: "Rejected" },
  skipped: {
    icon: TbPlayerSkipForward,
    color: "neutral.500",
    label: "Step skipped",
  },
  provisioning: {
    icon: TbSettings,
    color: "azurite.500",
    label: "Provisioning access",
  },
  provisioned: { icon: TbKey, color: "success.600", label: "Access provisioned" },
  deprovisioning: {
    icon: TbSettings,
    color: "warning.500",
    label: "Removing access",
  },
  deprovisioned: {
    icon: TbUserMinus,
    color: "error.500",
    label: "Access removed",
  },
  cancelled: { icon: TbCircleX, color: "neutral.600", label: "Request cancelled" },
  info: { icon: TbInfoCircle, color: "azurite.500", label: "Update" },
  failed: { icon: TbAlertTriangle, color: "error.500", label: "Failed" },
};

// Real per-request event log → the activity timeline, newest-first.
export const toTimeline = (
  request?: MFAccessRequest | null,
): TimelineEvent[] => {
  if (!request?.events?.length) return [];
  return request.events
    .map((e) => {
      const meta = EVENT_META[e.status];
      return {
        id: e.id,
        icon: meta.icon,
        color: meta.color,
        label: meta.label,
        detail: e.message,
        at: e.occuredAt,
      };
    })
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
};

export interface DetailCardHistory {
  priorRequests?: MFPriorRequests | null;
  teamAdoption?: MFTeamAdoption | null;
  /** Resource-card sub-line — "Catalog Item: X · Catalog: Y". */
  catalogItemName?: string | null;
  catalogName?: string | null;
}

// Show History only when the server supplied real insight data (prior requests
// with at least one decision, or team adoption) — never empty scaffolding.
export const hasHistory = (h: DetailCardHistory): boolean =>
  Boolean(
    (h.priorRequests && h.priorRequests.approved + h.priorRequests.denied > 0) ||
      h.teamAdoption,
  );

// Short, safe date — "—" for missing/invalid values.
export const fmtDate = (iso?: string | null): string =>
  iso && !Number.isNaN(new Date(iso).getTime())
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";

// Date + time — "Jul 10, 2026, 3:41 PM". Used to disambiguate a same-day grant
// window. "—" for missing/invalid values.
export const fmtDateTime = (iso?: string | null): string =>
  iso && !Number.isNaN(new Date(iso).getTime())
    ? new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "-";

// "Request sent N days ago" — `today` for <1 day.
export const sentLabel = (createdAt: string): string => {
  const days = Math.floor(
    (Date.now() - new Date(createdAt).getTime()) / DAY_MS,
  );
  if (days <= 0) return "Request sent today";
  return `Request sent ${days} ${days === 1 ? "day" : "days"} ago`;
};

// A person's display name from first/last, trimmed. Empty string when absent.
export const fullName = (m?: {
  firstName?: string;
  lastName?: string;
} | null): string =>
  m ? [m.firstName, m.lastName].filter(Boolean).join(" ").trim() : "";
